/**
 * @file Zoom setup functionality for the Padlet app.
 */

import device from '@@/bits/device'
import type { BeethovenContentCategory, BeethovenContentSubcategory } from '@padlet/beethoven-client/lib/types'

export enum ZoomCollaborateStatus {
  notStarted = 'notStarted',
  started = 'started',
  stopped = 'stopped',
  joined = 'joined',
  left = 'left',
}

export type ZoomRole = 'host' | 'coHost' | 'attendee' | 'panelist'

export async function initializeZoomSdk(): Promise<void> {
  const zoomSdk = (await import('@zoom/appssdk')).default
  await zoomSdk.config({
    popoutSize: { width: 480, height: 360 },
    capabilities: [
      'openUrl',
      'getRunningContext',
      'getUserContext',
      'expandApp',
      'startCollaborate',
      'onCollaborateChange',
    ],
  })
}

export async function checkIfUserIsZoomAttendee(): Promise<boolean> {
  if (device.zoomApp === false) {
    return false
  }
  const zoomSdk = (await import('@zoom/appssdk')).default
  try {
    await initializeZoomSdk()
    const userContext = await zoomSdk.getUserContext()
    return userContext.role === 'attendee'
  } catch (e) {
    return false
  }
}

export function isAttachmentSupportedByZoom(displayAttributes: {
  contentCategory?: BeethovenContentCategory | null
  contentSubcategory?: BeethovenContentSubcategory | null
  providerName?: string
}): boolean {
  // Return false for padlets because we want to open them within the Zoom window,
  // not in the browser
  if (displayAttributes.contentSubcategory === 'padlet') return false

  if (displayAttributes.contentCategory == null || displayAttributes.providerName == null) return true // Required for type check
  return (
    ['page', 'audio', 'document'].includes(displayAttributes.contentCategory) ||
    (displayAttributes.contentCategory === 'video' &&
      !['youtube', 'vimeo'].includes(displayAttributes.providerName.toLowerCase()))
  )
}

// Open url in the browser as we don't want to open it in the zoom app
// Zoom doesn't support navigation or embeds without OWASP headers
export async function openUrlInBrowserFromZoom(url: string): Promise<void> {
  const zoomSdk = (await import('@zoom/appssdk')).default
  zoomSdk.openUrl({ url }).catch(() => {})
}
